import React from 'react';
import { FreeNote } from '../common/GuideCta';
import { Link } from 'react-router-dom';
import { useT } from '../../../i18n';
import MarketingHeader from '../common/MarketingHeader';
import MarketingFooter from '../common/MarketingFooter';
import CtaBand from '../Home/CtaBand';
import { ALL_GUIDES, GUIDE_THEMES } from '../common/guideIndex';
import { AUTHOR, AUTHOR_URL } from '../common/author';
import ThemeGuides from '../common/ThemeGuides';
import '../../../styles/marketing.css';
import '../../../styles/marketing-guide.css';

// Page auteur : qui écrit les guides, pourquoi le site existe, et tous ses articles
// (données structurées ProfilePage + Person, reprises par les articles).
const AuthorPage: React.FC = () => {
  const t = useT();
  const ld = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: AUTHOR.name,
      url: AUTHOR_URL,
      image: `https://www.please-rsvp.com${AUTHOR.photo}`,
      jobTitle: AUTHOR.role,
      worksFor: [
        { '@type': 'Organization', name: 'RSVP Please', url: 'https://www.please-rsvp.com/' },
        { '@type': 'Organization', name: AUTHOR.company.name, url: AUTHOR.company.url },
      ],
      sameAs: [AUTHOR.linkedin],
      knowsAbout: ['Organisation de mariage', 'Budget mariage', 'Faire-part et invitations', 'Plan de table'],
    },
  });

  return (
    <div className="mk-page">
      <MarketingHeader>
        <Link to="/login" className="mkt-btn ghost">
          {t('Se connecter')}
        </Link>
        <Link to="/signup" className="mkt-btn primary mk-header-signup">
          {t('Créer mon espace')}
        </Link>
      </MarketingHeader>

      <main className="mk-main" lang="fr">
        <article className="mg-article mk-container">
          <header className="mg-author-hero">
            <div className="mg-article-head">
              <span className="mk-eyebrow">{AUTHOR.role}</span>
              <h1>{AUTHOR.name}</h1>
              <p className="mg-article-lead">
                Mariée le 15 août 2026, j’ai créé RSVP Please, le site et l’application, pour que les futurs mariés
                passent moins de temps dans les tableurs et plus de temps à profiter de leurs préparatifs.
              </p>
              <p className="mg-author-links">
                <a href={AUTHOR.linkedin} target="_blank" rel="noopener noreferrer me">
                  LinkedIn
                </a>
                <a href={AUTHOR.company.url} target="_blank" rel="noopener noreferrer">
                  {AUTHOR.company.name}
                </a>
              </p>
            </div>
            <img
              className="mg-author-photo"
              src={AUTHOR.photo}
              alt={`${AUTHOR.name} le jour de son mariage`}
              width={720}
              height={900}
            />
          </header>

          <section id="histoire">
            <h2>Pourquoi j’ai créé RSVP Please</h2>
            <p>
              En préparant notre mariage, j’ai vite découvert l’envers du décor : la liste d’invités dans un tableur, les
              réponses éparpillées entre SMS, WhatsApp et e-mails, les relances, le plan de table refait dix fois, et un
              budget qu’on ne voit jamais en entier.
            </p>
            <p>
              Les outils existants faisaient chacun un petit morceau, souvent avec de la publicité ou un abonnement. J’ai
              donc construit celui que j’aurais aimé avoir : un seul espace, gratuit, avec le site de mariage, les réponses
              en ligne, la liste d’invités, le plan de table, le budget et le rétroplanning, qui se parlent entre eux.
            </p>
            <p>
              Coder des logiciels, c’est mon métier : ingénieure de formation, je suis développeuse et cofondatrice de{' '}
              <a href={AUTHOR.company.url} target="_blank" rel="noopener noreferrer">
                {AUTHOR.company.name}
              </a>
              , où nous en concevons au quotidien. RSVP Please, c’est ce savoir-faire mis au service des futurs mariés.
            </p>
          </section>

          <section id="commencer" className="mg-author-cta" aria-labelledby="commencer-title">
            <h2 id="commencer-title">Commencez où que vous en soyez</h2>
            <p>
              Créer votre espace prend quelques minutes et ne coûte rien. Il s’adapte à l’avancement de vos préparatifs :
              vous utilisez ce dont vous avez besoin maintenant, le reste vous attend.
            </p>
            <ol className="mg-stages">
              <li>
                <b>Pas encore de date</b>
                <span>Un rétroplanning pour savoir quoi faire et quand, un budget, une première liste d’invités.</span>
              </li>
              <li>
                <b>La date est fixée</b>
                <span>Votre site de mariage, les faire-part et save the date, les réponses de vos invités en ligne.</span>
              </li>
              <li>
                <b>La dernière ligne droite</b>
                <span>Le plan de table à partir des invités qui ont répondu oui, et le déroulé de la journée.</span>
              </li>
            </ol>
            <Link to="/signup" className="mkt-btn primary mk-btn-lg">
              Créer mon espace gratuit
            </Link>
            <FreeNote />
          </section>

          <section id="guides-promesse">
            <h2>Ce que vous trouverez dans mes guides</h2>
            <ul className="mg-bullets">
              <li>
                <b>Une réponse directe</b> dès les premières lignes, sans détour.
              </li>
              <li>
                <b>Des chiffres concrets</b> : des fourchettes de prix indicatives, des quantités, des délais.
              </li>
              <li>
                <b>Des textes prêts à copier</b> pour vos faire-part, relances, remerciements et discours.
              </li>
              <li>
                <b>Des sources officielles</b> pour tout ce qui touche aux démarches, comme{' '}
                <a href="https://www.service-public.fr/particuliers/vosdroits/N142">service-public.fr</a>.
              </li>
            </ul>
            <p>
              Je les mets à jour au fil des retours des couples qui utilisent RSVP Please. Retrouvez-les tous sur la page{' '}
              <Link to="/guides">{ALL_GUIDES.length} guides et outils gratuits</Link>.
            </p>
          </section>

          {GUIDE_THEMES.map((theme) => (
            <ThemeGuides key={theme.id} theme={theme} showText={false} />
          ))}

          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: ld }} />
        </article>

        <CtaBand />
      </main>

      <MarketingFooter />
    </div>
  );
};

export default AuthorPage;
